/**
 * Which test counts as "in progress".
 *
 * Extracted from TestsStore so it can actually be tested: TestsStore transitively
 * imports appsConfig, which pulls in image assets, so the node test runner cannot load
 * it. This predicate had a real bug that only a code review caught — worth pinning.
 *
 * The bug: the resume banner originally read `activeTest`, but `reviewTest()` ALSO
 * assigns `activeTest` — with a COMPLETED test, because review reuses the /test route
 * — and nothing clears it when the student leaves review. So a student mid-exam who
 * opened a past result lost the banner, which was their only way back into the attempt,
 * while the exam sat intact in localStorage.
 */

/** The minimum shape this predicate needs. Keeps it independent of the Test class. */
export interface InProgressCandidate {
  isFinished: boolean;
}

/**
 * Resolves the in-progress test, or `undefined`.
 *
 * `isFinished` is load-bearing, not belt-and-braces: without it a completed test opened
 * in review would be reported as in progress.
 */
export function selectInProgressTest<T extends InProgressCandidate>(
  tests: ReadonlyMap<string, T>,
  inProgressTestId: string | null,
): T | undefined {
  if (!inProgressTestId) return undefined;

  const test = tests.get(inProgressTestId);
  return test && !test.isFinished ? test : undefined;
}
